import React, { FC, memo, useEffect, useMemo, useState } from 'react';

import { useIntl } from 'react-intl';

import { IncentiveDebugTool } from 'components/debug-loyalty/incentive-debug';
import withAddIncentiveToCart from 'components/with-add-incentive-to-cart';
import { RedemptionMethod } from 'components/with-add-incentive-to-cart/types';
import { LogUserErrorSeverity, logUserErrorMessage } from 'hooks/log-user-error-message';
import { useNavigation } from 'hooks/navigation/use-navigation';
import { useRoute } from 'hooks/navigation/use-route';
import { useCart } from 'hooks/use-cart';
import { StyledLoyaltyLoader } from 'pages/loyalty/loyalty-offers/loyalty-offers.styled';
import { useAuthContext } from 'state/auth';
import { actions, selectors, useAppDispatch, useAppSelector } from 'state/global-state';
import { removeAppliedRewardsInStorage } from 'state/global-state/models/loyalty/rewards/rewards.utils';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { useLocationContext } from 'state/location';
import { useInRestaurantRedemptionContext } from 'state/loyalty/in-restaurant-redemption';
import { isLoyaltyOffer, isReward, isValidOfferIncentive } from 'state/loyalty/types';
import { CustomEventNames, EventTypes, useMParticleContext } from 'state/mParticle';
import { useOrderContext } from 'state/order';
import { useServiceModeContext } from 'state/service-mode';
import { useStoreContext } from 'state/store';
import { routes } from 'utils/routing';

import { SHOULD_SELECT_STORE } from '../constants';
import { parseIncentiveData } from '../incentive-card/parse-incentive-data';
import { useLoyaltyIncentivesAvailability } from '../use-loyalty-incentives-availability';

import { ONLY_SHOW_REDEEM_REWARD_IN_RESTAURANT_CTA } from './constants';
import { IncentiveDetailsView } from './incentive-details.view';
import { IIncentiveDetailsProps, IIncentiveDetailsViewProps } from './types';

/**
 *
 * IncentiveDetails displays an expanded view of a incentive when selected from the incentive list
 *
 */
const IncentiveDetails: FC<React.PropsWithChildren<IIncentiveDetailsProps>> = ({
  selectedIncentive,
  engineIncentivesMap,
  addIncentiveToCart,
  showUnavailableDefaultMessage,
}) => {
  const incentiveLoyaltyEngineId = selectedIncentive?.loyaltyEngineId || '';
  const selectedIncentiveId = selectedIncentive?._id || '';
  const engineReward =
    engineIncentivesMap[incentiveLoyaltyEngineId] || engineIncentivesMap[selectedIncentiveId];

  const {
    pointCost,
    locked,
    redemptionPercentage,
    remainingPointsNeededForRedemption,
    rewardBenefitId,
  } = engineReward || {};

  const {
    recent: { setPendingRecentItem },
  } = useOrderContext();

  const { logRBIEvent, logLoyaltyEvent } = useMParticleContext();
  const { params, pathname } = useRoute<{ [ONLY_SHOW_REDEEM_REWARD_IN_RESTAURANT_CTA]: string }>();
  const { removeFromCart } = useCart();
  const {
    inRestaurantLoyaltyEnabledAtRestaurant,
    clearInRestaurantRedemptionAllRewards,
    enableRewardRedemption,
  } = useInRestaurantRedemptionContext();
  const rewardData = parseIncentiveData(selectedIncentive);
  const { formatMessage } = useIntl();
  const [checkingAvailability, setCheckingAvailability] = useState(true);
  const [showRemoveRewardButton, setShowRemoveRewardButton] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const isDebugToolEnabled = useFlag(LaunchDarklyFlag.ENABLE_DEBUG_TOOL);
  const { isAuthenticated } = useAuthContext();

  const { incentiveId, incentiveType } =
    useMemo(() => {
      const incentive = isReward(selectedIncentive)
        ? selectedIncentive.incentives?.find(benefit => benefit?._id === rewardBenefitId)
        : selectedIncentive.incentives?.[0];
      return {
        incentiveId: isValidOfferIncentive(incentive) ? incentive?._id : '',
        incentiveType: incentive?._type,
      };
    }, [rewardBenefitId, selectedIncentive]) || {};
  const dispatch = useAppDispatch();
  const appliedLoyaltyRewardsArray = useAppSelector(
    selectors.loyalty.selectAppliedLoyaltyRewardsArray
  );
  const offersFeedbackMap = useAppSelector(selectors.loyalty.selectOfferFeedbackMap);
  const loyaltyOffersInCooldown = useAppSelector(selectors.loyalty.selectLoyaltyOffersInCooldown);

  const {
    checkIncentiveAvailability,
    incentiveUnavailableMessage,
  } = useLoyaltyIncentivesAvailability(selectedIncentive, pointCost, locked);
  const exceedsBalanceError = formatMessage({ id: 'exceedsRewardsBalance' });

  const { setStoreLocatorCallbackUrl } = useLocationContext();
  const { navigate } = useNavigation();
  const { noStoreSelected } = useStoreContext();
  const { serviceMode } = useServiceModeContext();

  const enabledForceRestaurantSelection = Boolean(
    useFlag(LaunchDarklyFlag.FORCE_RESTAURANT_SELECTION_FOR_REWARDS)
  );

  const isModalSignUpEnabled = !isAuthenticated;
  const shouldRedirectToStoreLocator =
    enabledForceRestaurantSelection && (noStoreSelected || !serviceMode) && !isModalSignUpEnabled;

  useEffect(() => {
    if (shouldRedirectToStoreLocator) {
      setStoreLocatorCallbackUrl(pathname);
      navigate(routes.storeLocator, { replace: true, popCurrent: true });
    }
  }, [navigate, pathname, setStoreLocatorCallbackUrl, shouldRedirectToStoreLocator]);

  useEffect(() => {
    if (showUnavailableDefaultMessage && isLoyaltyOffer(selectedIncentive)) {
      setErrorMessage(
        formatMessage(
          { id: 'incentiveFeedbackInvalidStore' },
          { incentiveType: formatMessage({ id: 'offer' }) }
        )
      );
      setCheckingAvailability(false);
      return;
    }
    if (incentiveUnavailableMessage) {
      setErrorMessage(incentiveUnavailableMessage);
      setShowRemoveRewardButton(incentiveUnavailableMessage === exceedsBalanceError);
      setCheckingAvailability(false);
      return;
    }
    if (noStoreSelected) {
      setErrorMessage('');
      setCheckingAvailability(false);
      return;
    }

    // Check incentive item availability in selected store
    (async () => {
      setErrorMessage('');
      try {
        const isAvailable = await checkIncentiveAvailability(`${incentiveType}-${incentiveId}`);
        const storeError = isAvailable ? '' : formatMessage({ id: 'nonAvailableItemInStore' });

        setErrorMessage(storeError);
        setCheckingAvailability(false);

        if (storeError) {
          logUserErrorMessage({
            message: storeError,
            severity: LogUserErrorSeverity.Recoverable,
            context: {
              incentiveType,
              incentiveId,
              incentiveLoyaltyEngineId,
              displayName: (selectedIncentive as any)?.name?.localeRaw?.[0]?.children?.[0]?.text,
            },
          });
        }
      } catch {
        setCheckingAvailability(false);
      }
    })();
  }, [
    incentiveLoyaltyEngineId,
    incentiveId,
    incentiveType,
    checkIncentiveAvailability,
    exceedsBalanceError,
    formatMessage,
    noStoreSelected,
    incentiveUnavailableMessage,
    selectedIncentive,
  ]);

  const logIncentiveDetails = (isAvailable: boolean) => {
    if (!selectedIncentive) {
      return;
    }

    // Log Reward event
    if (!isLoyaltyOffer(selectedIncentive)) {
      logLoyaltyEvent(
        {
          name: CustomEventNames.REWARD_DETAILS_VIEWED,
          incentives: [selectedIncentive],
        },
        { skipLoggingToBraze: true }
      );

      return;
    }

    // Log Offer event
    logLoyaltyEvent({
      name: CustomEventNames.OFFER_DETAILS_VIEWED,
      incentives: [selectedIncentive],
      attributes: { isAvailable },
    });
  };

  const incentiveEvaluationResults = offersFeedbackMap[incentiveLoyaltyEngineId];
  const handleRemoveRewardsPress = () => {
    if (appliedLoyaltyRewardsArray?.length) {
      appliedLoyaltyRewardsArray.forEach(appliedReward => {
        removeFromCart(appliedReward);
        dispatch(
          actions.loyalty.removeAppliedReward({
            cartId: appliedReward.cartId,
            rewardBenefitId: appliedReward.rewardId,
          })
        );
      });
      clearInRestaurantRedemptionAllRewards();
      removeAppliedRewardsInStorage();

      setShowRemoveRewardButton(false);
    }
  };

  const onlyShowRedeemRewardInRestaurantCTA =
    String(params[ONLY_SHOW_REDEEM_REWARD_IN_RESTAURANT_CTA]) === 'true';

  const canAddToInRestaurantOrder =
    (!SHOULD_SELECT_STORE || inRestaurantLoyaltyEnabledAtRestaurant) &&
    ((isLoyaltyOffer(selectedIncentive) && !selectedIncentive.mobileOrderOnly) ||
      (isReward(selectedIncentive) &&
        enableRewardRedemption &&
        !selectedIncentive.mobileOrderOnly));

  const canAddToMobileOrder =
    !onlyShowRedeemRewardInRestaurantCTA &&
    (isLoyaltyOffer(selectedIncentive) || isReward(selectedIncentive));

  const logOfferAndRewardAddedToOrderEvent = (redemptionModeString: 'Online' | 'In-Store') => {
    const eventName = isLoyaltyOffer(selectedIncentive)
      ? CustomEventNames.OFFER_ADDED_TO_ORDER
      : CustomEventNames.REWARD_ADDED_TO_ORDER;
    let incentiveName = '';

    if (isLoyaltyOffer(selectedIncentive)) {
      incentiveName = selectedIncentive.name?.localeRaw[0]?.children[0]?.text;
    } else {
      incentiveName = selectedIncentive?.name?.locale ?? '';
    }

    logRBIEvent({
      name: eventName,
      type: EventTypes.Other,
      attributes: {
        engineId: selectedIncentive.loyaltyEngineId ?? '',
        sanityId: selectedIncentive._id,
        name: incentiveName,
        redemptionMode: redemptionModeString,
      },
    });
  };

  if (shouldRedirectToStoreLocator) {
    return <StyledLoyaltyLoader />;
  }

  const incentiveViewProps: IIncentiveDetailsViewProps = {
    ...rewardData,
    pointCost,
    locked,
    redemptionPercentage,
    handleRedeemRewardPress: () => {
      // First force reset pending recent items to prevent adding multiple items to the cart,
      // Then add the incentive to the cart
      setPendingRecentItem(null);
      addIncentiveToCart({ incentive: selectedIncentive, method: RedemptionMethod.MOBILE });
      logOfferAndRewardAddedToOrderEvent('Online');
    },
    handleInRestaurantRedemptionPress: () => {
      addIncentiveToCart({
        incentive: selectedIncentive,
        method: RedemptionMethod.IN_RESTAURANT,
      });
      logOfferAndRewardAddedToOrderEvent('In-Store');
    },
    remainingPointsNeededForRedemption,
    errorMessage,
    checkingAvailability,
    canAddToInRestaurantOrder,
    canAddToMobileOrder,
    showRemoveRewardButton,
    handleRemoveRewardsPress,
    incentiveEvaluationResults,
    offersInCooldown: loyaltyOffersInCooldown,
    onLogIncentiveDetailsEvent: logIncentiveDetails,
  };

  return (
    <>
      {isDebugToolEnabled && <IncentiveDebugTool incentive={selectedIncentive} />}
      <IncentiveDetailsView {...incentiveViewProps} />
    </>
  );
};

export default memo(withAddIncentiveToCart(IncentiveDetails));
